import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  Tag, Popconfirm, message, Space, Statistic, Row, Col, Card, Divider,
} from "antd";
import { ArrowLeft, Plus, ShoppingCart } from "lucide-react";
import dayjs from "dayjs";
import {
  usePOList, usePOStats, usePOCreate, usePOUpdate,
  usePODelete, usePOStatus, usePOCancel, useFinClients, useFinSuppliers,
  useAllQuotes,
} from "../../../api-hooks/finance";

const STATUS_TAG: Record<string, string> = {
  draft: "default", sent: "blue", acknowledged: "cyan",
  partially_fulfilled: "orange", fulfilled: "green", cancelled: "red",
};
const STATUS_OPTS = [
  { value: "draft", label: "Draft" }, { value: "sent", label: "Sent" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "partially_fulfilled", label: "Partially Fulfilled" },
  { value: "fulfilled", label: "Fulfilled" }, { value: "cancelled", label: "Cancelled" },
];

export default function PurchaseOrdersPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [page, setPage] = useState(1);
  const params = { search: search || undefined, status: statusF || undefined, page, limit: 20 };

  const { data: res, isLoading } = usePOList(cid, params);
  const { data: stats } = usePOStats(cid);
  const { data: clients = [] } = useFinClients(cid);
  const { data: suppliers = [] } = useFinSuppliers(cid);
  const { data: allQuotes = [] } = useAllQuotes(cid);
  // flow: "standalone" | "linked"
  const [poFlow, setPoFlow] = useState<"standalone" | "linked">("standalone");

  const pos = res?.data ?? [];
  const total = res?.total ?? 0;

  const [modal, setModal] = useState<{ open: boolean; rec: any | null; mode: "form" | "status" }>({ open: false, rec: null, mode: "form" });
  const [form] = Form.useForm();
  const [lineItems, setLineItems] = useState<any[]>([{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); closeModal(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = usePOCreate(cid, cb("PO created."));
  const update = usePOUpdate(cid, cb("PO updated."));
  const remove = usePODelete(cid, cb("Deleted."));
  const status = usePOStatus(cid, cb("Status updated."));
  const cancel = usePOCancel(cid, cb("Cancelled."));

  function openCreate() {
    setModal({ open: true, rec: null, mode: "form" });
    setPoFlow("standalone");
    setLineItems([{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);
    form.resetFields();
    form.setFieldsValue({ status: "draft", issue_date: dayjs(), vat_rate: 20 });
  }

  function openEdit(r: any) {
    setModal({ open: true, rec: r, mode: "form" });
    form.setFieldsValue({
      client_id: r.client_id, supplier_name: r.supplier_name, supplier_email: r.supplier_email,
      supplier_reference: r.supplier_reference, status: r.status,
      issue_date: r.issue_date ? dayjs(r.issue_date) : null,
      expected_delivery_date: r.expected_delivery_date ? dayjs(r.expected_delivery_date) : null,
      vat_rate: r.vat_rate, discount_amount: r.discount_amount, notes: r.notes,
    });
    setLineItems(r.line_items?.length ? r.line_items.map((li: any) => ({ description: li.description, quantity: li.quantity, unit_price: li.unit_price, vat_rate: li.vat_rate })) : [{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);
  }

  function openStatus(r: any) { setModal({ open: true, rec: r, mode: "status" }); form.resetFields(); }
  function closeModal() { setModal({ open: false, rec: null, mode: "form" }); form.resetFields(); }

  function handleSubmit(vals: any) {
    const payload = {
      ...vals,
      issue_date: vals.issue_date?.format("YYYY-MM-DD"),
      expected_delivery_date: vals.expected_delivery_date?.format("YYYY-MM-DD"),
      line_items: lineItems,
    };
    if (modal.mode === "status") { status.mutate({ id: modal.rec.id, status: vals.status }); return; }
    if (modal.rec) update.mutate({ id: modal.rec.id, ...payload });
    else create.mutate(payload);
  }

  const columns = [
    { title: "PO #", dataIndex: "po_number", key: "n", render: (v: string, r: any) => <div><div className="font-medium text-sm">{v}</div><div className="text-xs text-slate-400">{r.supplier_name}</div></div> },
    { title: "Client", dataIndex: "client_name", key: "c", render: (v: string) => v || "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_TAG[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "Issue", dataIndex: "issue_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Delivery", dataIndex: "expected_delivery_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Total", dataIndex: "total", key: "t", render: (v: number) => `£${Number(v || 0).toFixed(2)}` },
    {
      title: "", key: "act", render: (_: any, r: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Button size="small" onClick={() => openStatus(r)}>Status</Button>
          <Popconfirm title="Cancel PO?" onConfirm={() => cancel.mutate({ id: r.id, reason: "Cancelled by admin" })}><Button size="small" danger>Cancel</Button></Popconfirm>
          <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
        </Space>
      ),
    },
  ];

  const statsCards = stats ? [
    { title: "Draft", value: stats.draft ?? 0, color: "#6b7280" },
    { title: "Sent", value: stats.sent ?? 0, color: "#3b82f6" },
    { title: "Acknowledged", value: stats.acknowledged ?? 0, color: "#06b6d4" },
    { title: "Fulfilled", value: stats.fulfilled ?? 0, color: "#10b981" },
  ] : [];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-violet-600 hover:text-violet-800 flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
          <div>
            <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2"><ShoppingCart size={20} className="text-violet-600" /> Purchase Orders — Company #{cid}</h1>
            <p className="text-xs text-slate-500">{total} POs</p>
          </div>
        </div>
        <Button type="primary" icon={<Plus size={16} />} onClick={openCreate} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>New PO</Button>
      </div>

      {stats && (
        <Row gutter={12} className="mb-4">
          {statsCards.map(s => (
            <Col key={s.title} span={6}>
              <Card size="small" className="text-center"><Statistic title={s.title} value={s.value} valueStyle={{ color: s.color, fontSize: 20 }} /></Card>
            </Col>
          ))}
        </Row>
      )}

      <div className="flex gap-3 mb-4">
        <Input.Search placeholder="Search POs…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" allowClear />
        <Select value={statusF || undefined} onChange={v => setStatusF(v ?? "")} placeholder="All Statuses" options={[{ value: "", label: "All" }, ...STATUS_OPTS]} className="w-52" allowClear />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <Table rowKey="id" dataSource={pos} columns={columns} loading={isLoading} size="small"
          pagination={{ current: page, pageSize: 20, total, onChange: setPage, showTotal: t => `${t} POs` }} />
      </div>

      <Modal open={modal.open} onCancel={closeModal} footer={null} destroyOnClose width={modal.mode === "form" ? 640 : 380}
        title={modal.mode === "status" ? "Update Status" : modal.rec ? `Edit ${modal.rec.po_number}` : "New Purchase Order"}>
        <Form form={form} layout="vertical" size="middle" onFinish={handleSubmit} className="pt-2">
          {modal.mode === "form" && (<>
            {/* Flow selector — standalone vs linked to a quote */}
            <div className="mb-4 flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">PO Flow:</span>
              <Select
                value={poFlow}
                onChange={(v) => { setPoFlow(v); form.setFieldValue("quotation_id", undefined); }}
                options={[
                  { value: "standalone", label: "Standalone (no quote)" },
                  { value: "linked", label: "Linked to Quotation" },
                ]}
                style={{ width: 220 }}
                size="small"
              />
            </div>
            {poFlow === "linked" && (
              <Form.Item name="quotation_id" label="Linked Quotation (must be Accepted)" rules={[{ required: true, message: "Select a quotation" }]}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select accepted quotation…"
                  optionFilterProp="label"
                  options={(allQuotes as any[]).filter((q: any) => q.status === "accepted").map((q: any) => ({
                    value: q.id,
                    label: `${q.quote_number} — ${q.title || q.client_name || ""}`,
                  }))}
                />
              </Form.Item>
            )}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="client_id" label="Client"><Select showSearch allowClear options={(clients as any[]).map((c: any) => ({ value: c.id, label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email }))} /></Form.Item>
              <Form.Item name="supplier_name" label="Supplier Name">
                {(suppliers as any[]).length > 0
                  ? <Select showSearch allowClear options={(suppliers as any[]).map((s: any) => ({ value: s.name, label: s.name }))} />
                  : <Input placeholder="Supplier name" />}
              </Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="supplier_email" label="Supplier Email"><Input type="email" /></Form.Item>
              <Form.Item name="supplier_reference" label="Supplier Ref"><Input /></Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="issue_date" label="Issue Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
              <Form.Item name="expected_delivery_date" label="Expected Delivery"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            </div>
            <Divider plain className="my-2">Line Items</Divider>
            {lineItems.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 mb-2 items-start">
                <div className="col-span-5"><Input placeholder="Description" value={li.description} onChange={e => { const n = [...lineItems]; n[idx].description = e.target.value; setLineItems(n); }} /></div>
                <div className="col-span-2"><InputNumber className="w-full" placeholder="Qty" min={0} value={li.quantity} onChange={v => { const n = [...lineItems]; n[idx].quantity = v; setLineItems(n); }} /></div>
                <div className="col-span-2"><InputNumber className="w-full" placeholder="£" min={0} value={li.unit_price} onChange={v => { const n = [...lineItems]; n[idx].unit_price = v; setLineItems(n); }} /></div>
                <div className="col-span-2"><InputNumber className="w-full" placeholder="VAT%" min={0} max={100} value={li.vat_rate} onChange={v => { const n = [...lineItems]; n[idx].vat_rate = v; setLineItems(n); }} /></div>
                <div className="col-span-1"><Button size="small" danger onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}>×</Button></div>
              </div>
            ))}
            <Button size="small" onClick={() => setLineItems([...lineItems, { description: "", quantity: 1, unit_price: 0, vat_rate: 20 }])} className="mb-4">+ Line Item</Button>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="vat_rate" label="VAT %"><InputNumber className="w-full" min={0} max={100} /></Form.Item>
              <Form.Item name="discount_amount" label="Discount (£)"><InputNumber className="w-full" min={0} /></Form.Item>
            </div>
            <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          </>)}

          {modal.mode === "status" && (
            <Form.Item name="status" label="New Status" rules={[{ required: true }]}>
              <Select options={STATUS_OPTS} />
            </Form.Item>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95", borderColor: "#4c1d95" }} loading={create.isPending || update.isPending || status.isPending}>
              {modal.mode === "status" ? "Update" : modal.rec ? "Save" : "Create"}
            </Button>
            <Button onClick={closeModal}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
